import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Chip,
    useTheme as useMuiTheme,
    Divider
} from '@mui/material';
import { Close as CloseIcon, Info as InfoIcon, Warning as WarningIcon, Error as ErrorIcon, CheckCircle as SuccessIcon } from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface Log {
    id: number;
    user_id: number;
    user?: {
        id: number;
        name: string;
        email: string;
    };
    module: string;
    activity: string;
    description: string;
    ip_address: string;
    user_agent: string;
    created_at: string;
    updated_at: string;
}

interface LogViewProps {
    open: boolean;
    log: Log;
    onClose: () => void;
}

const LogView: React.FC<LogViewProps> = ({ open, log, onClose }) => {
    const muiTheme = useMuiTheme();
    const { theme: appTheme } = useTheme();

    const getActivityIcon = (activity: string) => {
        const activityLower = activity.toLowerCase();
        if (activityLower.includes('create') || activityLower.includes('add')) {
            return <SuccessIcon color="success" />;
        } else if (activityLower.includes('update') || activityLower.includes('edit')) {
            return <InfoIcon color="info" />;
        } else if (activityLower.includes('delete') || activityLower.includes('remove')) {
            return <ErrorIcon color="error" />;
        } else if (activityLower.includes('login') || activityLower.includes('logout')) {
            return <WarningIcon color="warning" />;
        }
        return <InfoIcon color="info" />;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: muiTheme.palette.background.paper,
                    border: `1px solid ${muiTheme.palette.divider}`
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                pb: 2
            }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Log Details
                </Typography>
                <Button
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        {getActivityIcon(log.activity)}
                        <Typography variant="h5" sx={{ 
                            fontWeight: 700, 
                            color: muiTheme.palette.text.primary
                        }}>
                            {log.activity}
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="textSecondary">
                        Module: {log.module}
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Activity Information */}
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="h6" sx={{ 
                            mb: 2, 
                            color: muiTheme.palette.text.primary, 
                            fontWeight: 600 
                        }}>
                            Activity Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Activity
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    {getActivityIcon(log.activity)}
                                    <Typography variant="body2">
                                        {log.activity}
                                    </Typography>
                                </Box>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Module
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                    <Chip
                                        label={log.module}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                </Box>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Description
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {log.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    User
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {log.user?.name || 'System'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    User Email
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {log.user?.email || 'N/A'}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    User ID
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {log.user_id}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Technical Information */}
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ 
                            mb: 2, 
                            color: muiTheme.palette.text.primary, 
                            fontWeight: 600 
                        }}>
                            Technical Information
                        </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    IP Address
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace" sx={{ mb: 1 }}>
                                    {log.ip_address}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Log ID
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {log.id}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Created At
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(log.created_at).toLocaleDateString()}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Created Time
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(log.created_at).toLocaleTimeString()}
                                </Typography>

                                <Typography variant="caption" color="textSecondary" display="block">
                                    Updated At
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    {new Date(log.updated_at).toLocaleString()}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* User Agent Information */}
                    {log.user_agent && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" sx={{ 
                                    mb: 2, 
                                    color: muiTheme.palette.text.primary, 
                                    fontWeight: 600 
                                }}>
                                    Browser Information
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Card sx={{ bgcolor: muiTheme.palette.background.paper, border: `1px solid ${muiTheme.palette.divider}` }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            User Agent
                                        </Typography>
                                        <Typography variant="body2" fontFamily="monospace" sx={{ 
                                            wordBreak: 'break-word',
                                            fontSize: '0.75rem'
                                        }}>
                                            {log.user_agent}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions sx={{ 
                p: 3, 
                pt: 2,
                borderTop: `1px solid ${muiTheme.palette.divider}`
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LogView;
